import { Component, Prop, Vue } from "vue-property-decorator";
import WithRender from "./SendersByNodesChart.html";
import Chart from "chart.js";
import { Inject } from "vue-di-container";
import RequestsSendersService from "../RequestsSendersService";
import { SENDERS_FILTER_KEY } from "../sendersDetailsShared";
import {
    buildMonitorDashboardBaseTooltipStyling,
    buildMonitorDashboardCategoryAxisTicks,
    buildMonitorDashboardResponsiveChartOptions,
    buildMonitorDashboardValueAxisTicks,
    getMonitorDashboardFontFamily,
    getMonitorDashboardChartColor,
    monitorDashboardDarkerBorder,
} from "../../../chart/monitorDashboardChartKit";

@WithRender
@Component({
    diProvide: [RequestsSendersService],
})
export default class SendersByNodesChart extends Vue {
    @Inject(RequestsSendersService)
    public requestsSendersService!: RequestsSendersService;

    @Prop({ required: true }) public filter!: any;
    @Prop({ required: true }) public tenants!: any[];

    private chart: any = null;
    private canvasId: string = "senders-by-nodes-chart-canvas";

    public hasData: boolean = true;
    public isLoading: boolean = false;
    private requestVersion: number = 0;
    private barCounts: number[] = [];
    private barTenantIds: string[] = [];

    private readonly emptyGuid: string = "00000000-0000-0000-0000-000000000000";
    private readonly fixedNodes: string[] = ["STC", "Mobily", "Zain"];

    public beforeDestroy(): void {
        this.destroyChart();
    }

    public onTitleClick(): void {
        this.navigateToDetails(null);
    }

    private navigateToDetails(tenantId: string | null): void {
        const f = this.filter;
        const sendersPayload = {
            dateFrom: f.dateFrom,
            dateTo: f.dateTo,
            tenantId: f.tenant,
            providerId: f.providerId,
            customerType: f.customerType || 0,
        };

        try {
            sessionStorage.setItem(
                SENDERS_FILTER_KEY,
                JSON.stringify(sendersPayload),
            );
        } catch (e) {
        }

        const query: any = {};
        if (tenantId && tenantId !== this.emptyGuid) {
            query.chartTenantId = tenantId;
        }

        this.$router.push({
            name: "SendersByNodesDetails",
            query: query,
        });
    }

    private onChartClick(evt: any, elements: any[]): void {
        if (!elements || !elements.length) {
            return;
        }

        const index = elements[0]._index != null ? Number(elements[0]._index) : -1;
        if (index < 0 || index >= this.barTenantIds.length) {
            return;
        }

        const count = this.barCounts.length > index ? Number(this.barCounts[index]) : 0;
        if (!count || count <= 0) {
            return;
        }

        const tenantId = this.barTenantIds[index] ? String(this.barTenantIds[index]) : "";
        if (!tenantId || tenantId === this.emptyGuid) {
            return;
        }

        this.navigateToDetails(tenantId);
    }

    private destroyChart(): void {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    public async refresh(): Promise<void> {
        if (!this.filter) return;

        this.isLoading = true;
        const currentRequestVersion = ++this.requestVersion;

        const resp = await this.requestsSendersService.GetSendersByNodes(this.filter);
        if (currentRequestVersion !== this.requestVersion) {
            return;
        }

        this.isLoading = false;

        const rows =
            resp && resp.result && Array.isArray(resp.result) ? resp.result : [];
        if (!resp || !resp.result || !Array.isArray(resp.result)) {
            this.hasData = false;
            this.destroyChart();
            return;
        }

        const nameToId: any = {};
        if (Array.isArray(this.tenants)) {
            for (let i = 0; i < this.tenants.length; i++) {
                const t = this.tenants[i];
                if (t && t.name && t.id && String(t.id) !== this.emptyGuid) {
                    nameToId[String(t.name).trim()] = String(t.id);
                }
            }
        }

        const countsById: any = {};
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (!r) continue;

            const tid = r.tenantId ? String(r.tenantId) : "";
            const c = r.count != null ? Number(r.count) : 0;

            if (tid) countsById[tid] = c;
        }

        const bars: { name: string; count: number; tenantId: string }[] = [];
        for (let i = 0; i < this.fixedNodes.length; i++) {
            const name = this.fixedNodes[i];
            const id = nameToId[name] ? nameToId[name] : "";
            const count = id && countsById.hasOwnProperty(id) ? Number(countsById[id]) : 0;
            bars.push({ name: name, count: count, tenantId: id });
        }

        bars.sort(function (a, b) { return b.count - a.count; });

        const total = bars.reduce(function (s, x) { return s + x.count; }, 0);
        this.hasData = total > 0;
        this.barCounts = bars.map(function (b) { return b.count; });
        this.barTenantIds = bars.map(function (b) { return b.tenantId; });

        await this.$nextTick();
        this.renderChart(bars);
    }

    private renderChart(bars: { name: string; count: number; tenantId: string }[]): void {
        this.destroyChart();

        if (!this.hasData) return;

        const el = document.getElementById(this.canvasId) as HTMLCanvasElement;
        if (!el) return;

        const ctx = el.getContext("2d") as any;

        const labels = bars.map(function (b) { return b.name; });
        const values = bars.map(function (b) { return b.count; });

        const yMax = this.computeNiceYAxisMax(values);

        const self = this;
        const fontFamily = getMonitorDashboardFontFamily();
        const baseOpts = buildMonitorDashboardResponsiveChartOptions();
        const fills: string[] = [];
        const borders: string[] = [];
        for (let i = 0; i < bars.length; i++) {
            const fill = getMonitorDashboardChartColor("sendersByNodes", bars[i].name);
            fills.push(fill);
            borders.push(monitorDashboardDarkerBorder(fill));
        }

        this.chart = new Chart(ctx, {
            type: "bar",
            data: {
                labels: labels,
                datasets: [
                    {
                        label: "",
                        data: values,
                        backgroundColor: fills,
                        borderColor: borders,
                        borderWidth: 2,
                        categoryPercentage: 0.52,
                        barPercentage: 0.65,
                    },
                ],
            },
            plugins: [
                {
                    afterDatasetsDraw: function (chart: any) {
                        self.drawZeroValueMarkers(chart);
                        self.drawTopValueLabels(chart);
                    },
                },
            ],
            options: Object.assign({}, baseOpts, {
                legend: { display: false },
                onClick: function (evt: any, elements: any[]) {
                    self.onChartClick(evt, elements);
                },
                scales: {
                    xAxes: [{
                        gridLines: { display: false },
                        ticks: buildMonitorDashboardCategoryAxisTicks(12, fontFamily),
                    }],
                    yAxes: [{
                        ticks: buildMonitorDashboardValueAxisTicks(11, fontFamily, true, yMax, undefined),
                    }],
                },
                tooltips: Object.assign({}, buildMonitorDashboardBaseTooltipStyling(), {
                    enabled: true,
                    callbacks: {
                        title: function (items: any) {
                            if (!items || !items.length) return "";
                            return String(items[0].xLabel || "");
                        },
                        label: function (item: any, data: any) {
                            const idx = item.index;
                            const lines: string[] = [];
                            let total = 0;
                            for (let i = 0; i < data.datasets.length; i++) {
                                const ds = data.datasets[i];
                                const raw = ds && ds.data && ds.data.length > idx ? Number(ds.data[idx]) : 0;
                                const value = isNaN(raw) ? 0 : raw;
                                total += value;
                                const dsLabel = ds && ds.label ? String(ds.label) : String(self.$t("shared.senders"));
                                lines.push(dsLabel + ": " + String(value));
                            }
                            if (data.datasets.length > 1) {
                                lines.push(String(self.$t("monitorDashboard.total")) + ": " + String(total));
                            }
                            return lines;
                        },
                    },
                }),
            }),
        });
    }

    private drawTopValueLabels(chart: any): void {
        const ctx2: any = chart.ctx;

        ctx2.save();
        ctx2.textAlign = "center";
        ctx2.textBaseline = "bottom";
        ctx2.font = "10px " + getMonitorDashboardFontFamily();
        ctx2.fillStyle = "#6b7280";

        const meta = chart.getDatasetMeta(0);

        for (let i = 0; i < meta.data.length; i++) {
            const val = Number(chart.data.datasets[0].data[i]);
            const bar = meta.data[i];

            let y = bar._model.y - 6;

            if (val === 0) {
                const yZero = chart.scales["y-axis-0"].getPixelForValue(0);
                y = yZero - 6;
            }

            ctx2.fillText(String(val), bar._model.x, y);
        }

        ctx2.restore();
    }

    private drawZeroValueMarkers(chart: any): void {
        const ctx2: any = chart.ctx;
        const yAxis = chart.scales["y-axis-0"];
        if (!yAxis) return;

        const yZero = yAxis.getPixelForValue(0);
        const meta = chart.getDatasetMeta(0);
        const colors = chart.data.datasets[0].borderColor;

        ctx2.save();
        ctx2.lineWidth = 3;

        for (let i = 0; i < meta.data.length; i++) {
            const val = Number(chart.data.datasets[0].data[i]);
            if (val !== 0) continue;

            const bar = meta.data[i];
            const model = bar._model;
            const x = model.x;
            const halfWidth = Math.max(1, (model.width || 0) / 2);

            ctx2.strokeStyle = Array.isArray(colors) ? colors[i] || colors[0] : colors;
            ctx2.beginPath();
            ctx2.moveTo(x - halfWidth, yZero);
            ctx2.lineTo(x + halfWidth, yZero);
            ctx2.stroke();
        }

        ctx2.restore();
    }

    private computeNiceYAxisMax(values: number[]): number {
        let maxVal = 0;
        for (let i = 0; i < values.length; i++) {
            const v = Number(values[i]);
            if (v > maxVal) maxVal = v;
        }
        if (maxVal <= 0) return 10;

        const rounded = Math.ceil(maxVal / 100) * 100;
        return rounded < 10 ? 10 : rounded;
    }
}
